// ============================================================================
// BP-04-02: Documents Frontend - DocumentViewer Component
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Download,
  Share2,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize,
  Minimize,
  Loader2,
  FileText,
  AlertCircle,
  Clock,
  User,
  Tag,
  Building2,
  Calendar,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { Document } from '../types/documentsTypes';
import { DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_COLORS } from '../types/documentsTypes';
import { formatFileSize } from '../hooks/useDocumentUpload';
import { useDocumentPreviewUrl, useDocumentDownloadUrl } from '../hooks/useDocuments';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface DocumentViewerProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (document: Document) => void;
  onEdit?: (document: Document) => void;
  onDelete?: (document: Document) => void;
  onShare?: (document: Document) => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isPreviewable(mimeType: string): boolean {
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/')
  );
}

// -----------------------------------------------------------------------------
// DocumentViewer Component
// -----------------------------------------------------------------------------

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  isOpen,
  onClose,
  onDownload,
  onEdit,
  onDelete,
  onShare,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false,
}) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'details'>('preview');

  // Fetch preview URL
  const {
    data: previewData,
    isLoading: isLoadingPreview,
    error: previewError,
  } = useDocumentPreviewUrl(document?.id || '', isOpen && !!document);

  // Reset zoom and rotation when document changes
  useEffect(() => {
    setZoom(100);
    setRotation(0);
  }, [document?.id]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasPrev) onNavigatePrev?.();
          break;
        case 'ArrowRight':
          if (hasNext) onNavigateNext?.();
          break;
        case '+':
        case '=':
          setZoom((z) => Math.min(z + 25, 300));
          break;
        case '-':
          setZoom((z) => Math.max(z - 25, 25));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasPrev, hasNext, onClose, onNavigatePrev, onNavigateNext]);

  // Handle download
  const handleDownload = useCallback(async () => {
    if (!document) return;
    onDownload?.(document);
  }, [document, onDownload]);

  if (!isOpen || !document) return null;

  const canPreview = isPreviewable(document.mime_type);
  const typeColor = DOCUMENT_TYPE_COLORS[document.document_type];
  const typeLabel = DOCUMENT_TYPE_LABELS[document.document_type];

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Viewer container */}
      <div
        className={cn(
          'relative flex flex-col w-full h-full bg-navy',
          isFullscreen ? 'p-0' : 'p-4 md:p-8'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-4 text-white">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-medium truncate max-w-md">
                {document.title}
              </h2>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <span
                  className="px-2 py-0.5 rounded-full text-xs text-white"
                  style={{ backgroundColor: typeColor }}
                >
                  {typeLabel}
                </span>
                <span>{formatFileSize(document.file_size_bytes)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {canPreview && (
              <>
                <button
                  onClick={() => setZoom((z) => Math.max(z - 25, 25))}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Zoom out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm min-w-[50px] text-center">{zoom}%</span>
                <button
                  onClick={() => setZoom((z) => Math.min(z + 25, 300))}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Zoom in"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Rotate"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-white/20" />
              </>
            )}

            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <Minimize className="w-5 h-5" />
              ) : (
                <Maximize className="w-5 h-5" />
              )}
            </button>

            <button
              onClick={handleDownload}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>

            {onShare && (
              <button
                onClick={() => onShare(document)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Share"
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}

            {onEdit && (
              <button
                onClick={() => onEdit(document)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Edit"
              >
                <Pencil className="w-5 h-5" />
              </button>
            )}

            {onDelete && (
              <button
                onClick={() => onDelete(document)}
                className="p-2 rounded-lg hover:bg-white/10 text-red-400 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex gap-4 min-h-0">
          {/* Preview area */}
          <div className="flex-1 relative bg-navy-light rounded-xl overflow-hidden">
            {/* Navigation arrows */}
            {hasPrev && (
              <button
                onClick={onNavigatePrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            {hasNext && (
              <button
                onClick={onNavigateNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Content */}
            <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
              {isLoadingPreview ? (
                <div className="flex flex-col items-center gap-3 text-white/60">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p>Loading preview...</p>
                </div>
              ) : previewError || !canPreview ? (
                <div className="flex flex-col items-center gap-3 text-white/60">
                  <FileText className="w-16 h-16" />
                  <p className="text-center">
                    {previewError
                      ? 'Failed to load preview'
                      : 'Preview not available for this file type'}
                  </p>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download to view
                  </button>
                </div>
              ) : document.mime_type === 'application/pdf' ? (
                <iframe
                  src={previewData?.url}
                  className="w-full h-full rounded-lg"
                  style={{
                    transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                  }}
                  title={document.title}
                />
              ) : (
                <img
                  src={previewData?.url || document.preview_url || ''}
                  alt={document.title}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  style={{
                    transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease-out',
                  }}
                />
              )}
            </div>
          </div>

          {/* Details panel */}
          {!isFullscreen && (
            <div className="w-80 flex-shrink-0 bg-white rounded-xl p-4 overflow-y-auto">
              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-cream rounded-lg mb-4">
                <button
                  onClick={() => setActiveTab('preview')}
                  className={cn(
                    'flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors',
                    activeTab === 'preview'
                      ? 'bg-white text-navy shadow-sm'
                      : 'text-warm-gray hover:text-navy'
                  )}
                >
                  Info
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  className={cn(
                    'flex-1 py-1.5 px-3 text-sm font-medium rounded-md transition-colors',
                    activeTab === 'details'
                      ? 'bg-white text-navy shadow-sm'
                      : 'text-warm-gray hover:text-navy'
                  )}
                >
                  Details
                </button>
              </div>

              {activeTab === 'preview' ? (
                <div className="space-y-4">
                  {/* Description */}
                  {document.description && (
                    <div>
                      <h4 className="text-xs font-medium text-warm-gray uppercase tracking-wide mb-1">
                        Description
                      </h4>
                      <p className="text-sm text-navy">{document.description}</p>
                    </div>
                  )}

                  {/* Provider */}
                  {document.provider_name && (
                    <div className="flex items-start gap-3">
                      <Building2 className="w-4 h-4 text-warm-gray mt-0.5" />
                      <div>
                        <h4 className="text-xs font-medium text-warm-gray uppercase tracking-wide mb-0.5">
                          Provider
                        </h4>
                        <p className="text-sm text-navy">{document.provider_name}</p>
                      </div>
                    </div>
                  )}

                  {/* Document date */}
                  {document.document_date && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-4 h-4 text-warm-gray mt-0.5" />
                      <div>
                        <h4 className="text-xs font-medium text-warm-gray uppercase tracking-wide mb-0.5">
                          Document Date
                        </h4>
                        <p className="text-sm text-navy">
                          {formatDate(document.document_date)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {document.tags.length > 0 && (
                    <div className="flex items-start gap-3">
                      <Tag className="w-4 h-4 text-warm-gray mt-0.5" />
                      <div>
                        <h4 className="text-xs font-medium text-warm-gray uppercase tracking-wide mb-1">
                          Tags
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {document.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-cream rounded-full text-xs text-navy"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Shared status */}
                  {document.is_shared_with_attorney && (
                    <div className="p-3 bg-sage/10 rounded-lg">
                      <div className="flex items-center gap-2 text-sage">
                        <Share2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Shared with Attorney</span>
                      </div>
                      {document.shared_at && (
                        <p className="text-xs text-warm-gray mt-1">
                          Shared on {formatDate(document.shared_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-warm-gray">File name</span>
                    <span className="text-navy font-medium truncate max-w-[150px]">
                      {document.original_filename}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-warm-gray">File type</span>
                    <span className="text-navy">{document.file_type.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-warm-gray">Size</span>
                    <span className="text-navy">{formatFileSize(document.file_size_bytes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-warm-gray">Version</span>
                    <span className="text-navy">{document.version}</span>
                  </div>
                  <div className="border-t border-warm-gray/10 pt-3">
                    <div className="flex justify-between">
                      <span className="text-warm-gray">Uploaded by</span>
                      <span className="text-navy">{document.uploaded_by_name}</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-warm-gray">Uploaded</span>
                    <span className="text-navy">{formatDateTime(document.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-warm-gray">Last modified</span>
                    <span className="text-navy">{formatDateTime(document.updated_at)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;

